import {
  type CreateItineraryComponentRequest,
  type CreateItineraryDayRequest,
  type CreateItineraryFromLeadRequest,
  type FlightPlan,
  FlightPlanSchema,
  type ItineraryAutoSearchPreviewRequest,
  type ItineraryAutoSearchPreviewResponse,
  ItineraryAutoSearchPreviewResponseSchema,
  type ItineraryReconcileResponse,
  ItineraryReconcileResponseSchema,
  type ItineraryApprovalActionRequest,
  type ItineraryApprovalListResponse,
  ItineraryApprovalListResponseSchema,
  type ItineraryComponentOut,
  ItineraryComponentOutSchema,
  type ItineraryDayOut,
  ItineraryDayOutSchema,
  type ItineraryPreviewLinkResponse,
  ItineraryPreviewLinkResponseSchema,
  type ItineraryRevisionOut,
  ItineraryRevisionOutSchema,
  type PublishItineraryRequest,
  type PublishItineraryResponse,
  type PublishPriceCheckResponse,
  PublishItineraryResponseSchema,
  PublishPriceCheckResponseSchema,
  type UpdateItineraryComponentRequest,
  type ItineraryDetail,
  ItineraryDetailSchema,
  type ItineraryListQuery,
  type ItineraryListResponse,
  ItineraryListResponseSchema,
  type MarkupSuggestion,
  MarkupSuggestionSchema,
  type UpdateItineraryDayRequest,
  type UpdateItineraryRequest,
  type LegRoomsOverride,
  type SaveItineraryPlanRequest,
  type ItineraryVersionSummary,
  ItineraryVersionListSchema,
  type ItineraryVersionDiff,
  ItineraryVersionDiffSchema,
  type RepriceRequest,
  type RepriceResponse,
  type FdRepriceRequest,
  RepriceResponseSchema,
  type RepriceApprovalListQuery,
  type RepriceApprovalListResponse,
  RepriceApprovalListResponseSchema,
  type RepriceApprovalListItem,
  RepriceApprovalListItemSchema,
  type InstalmentSplitProposeRequest,
  type InstalmentDateChangeProposeRequest,
  type RepriceApprovalApproveRequest,
  type RepriceApprovalRejectRequest,
  type RepriceApprovalPendingCount,
  RepriceApprovalPendingCountSchema,
  type VoidApprovalListQuery,
  type VoidApprovalListResponse,
  VoidApprovalListResponseSchema,
  type VoidApprovalListItem,
  VoidApprovalListItemSchema,
  type VoidApprovalCreateRequest,
  type VoidApprovalApproveRequest,
  type VoidApprovalRejectRequest,
  type VoidApprovalPendingCount,
  VoidApprovalPendingCountSchema,
} from '@utazzo/shared';
import { z } from 'zod';

import { apiClient } from '@/lib/api-client';

/**
 * Itinerary admin API. Day-level + component-level + approval + publish
 * sub-resources have their own clients added incrementally as those tabs
 * land in B5.D / B5.E / B5.F / B5.G.
 */

export const listItineraries = async (
  query: ItineraryListQuery,
): Promise<ItineraryListResponse> => {
  const { data } = await apiClient.get<unknown>('/itineraries', { params: query });
  return ItineraryListResponseSchema.parse(data);
};

export const getItinerary = async (id: string): Promise<ItineraryDetail> => {
  const { data } = await apiClient.get<unknown>(`/itineraries/${id}`);
  return ItineraryDetailSchema.parse(data);
};

/** Server-authoritative flight journey shape (round-trip bundle vs one-way). */
export const getItineraryFlightPlan = async (id: string): Promise<FlightPlan> => {
  const { data } = await apiClient.get<unknown>(`/itineraries/${id}/flight-plan`);
  return FlightPlanSchema.parse(data);
};

/** Auto-computed markup suggestion (quote engine) - drives PricingStep pre-fill. */
export const getMarkupSuggestion = async (id: string): Promise<MarkupSuggestion> => {
  const { data } = await apiClient.get<unknown>(`/itineraries/${id}/markup-suggestion`);
  return MarkupSuggestionSchema.parse(data);
};

/** Apply the markup suggestion: persists per-line component markup + returns totals. */
export const applyMarkup = async (id: string): Promise<MarkupSuggestion> => {
  const { data } = await apiClient.post<unknown>(`/itineraries/${id}/apply-markup`, {});
  return MarkupSuggestionSchema.parse(data);
};

/** Create an itinerary from a lead (the legacy "edit -> builder" entry point). */
export const createItineraryFromLead = async (
  leadId: string,
  payload: CreateItineraryFromLeadRequest,
): Promise<ItineraryDetail> => {
  const { data } = await apiClient.post<unknown>(`/leads/${leadId}/itineraries`, payload);
  return ItineraryDetailSchema.parse(data);
};

export const updateItinerary = async (
  id: string,
  payload: UpdateItineraryRequest,
): Promise<ItineraryDetail> => {
  const { data } = await apiClient.patch<unknown>(`/itineraries/${id}`, payload);
  return ItineraryDetailSchema.parse(data);
};

export const deleteItinerary = async (id: string): Promise<void> => {
  await apiClient.delete(`/itineraries/${id}`);
};

/**
 * Deactivate / re-activate an itinerary. When deactivated, the share URL and
 * PayU payment link go 404 for customers and the row drops out of the version
 * dropdown; re-activate to restore.
 */
export const setItineraryActive = async (id: string, active: boolean): Promise<void> => {
  await apiClient.post(`/itineraries/${id}/set-active`, { active });
};

/** Persist the Step-1 plan (legs + room groups) and re-sync days. */
export const saveItineraryPlan = async (
  id: string,
  payload: SaveItineraryPlanRequest,
): Promise<ItineraryDetail> => {
  const { data } = await apiClient.post<unknown>(`/itineraries/${id}/plan`, payload);
  return ItineraryDetailSchema.parse(data);
};

/**
 * Set or clear a stay's (city leg's) room-config override.
 * `rooms = null` reverts the stay to the itinerary's global room config.
 */
export const setLegRoomsOverride = async (
  itineraryId: string,
  legId: string,
  rooms: LegRoomsOverride | null,
): Promise<ItineraryDetail> => {
  const { data } = await apiClient.patch<unknown>(
    `/itineraries/${itineraryId}/legs/${legId}/rooms-override`,
    { rooms },
  );
  return ItineraryDetailSchema.parse(data);
};

/** Create a fresh version on the revision chain (blank or deep copy-forward); returns the new latest. */
export const createItineraryVersion = async (
  id: string,
  copyForward: boolean,
): Promise<ItineraryDetail> => {
  const { data } = await apiClient.post<unknown>(`/itineraries/${id}/versions`, { copyForward });
  return ItineraryDetailSchema.parse(data);
};

/** Re-open the current published version for editing (creates/reuses the draft). */
export const reviseItinerary = async (id: string): Promise<ItineraryDetail> => {
  const { data } = await apiClient.post<unknown>(`/itineraries/${id}/revise`, {});
  return ItineraryDetailSchema.parse(data);
};

/** All published sub-versions for a lead's itineraries, for the version-history + Won picker. */
export const listItineraryVersions = async (leadId: string): Promise<ItineraryVersionSummary[]> => {
  const { data } = await apiClient.get<unknown>(`/leads/${leadId}/itinerary-versions`);
  return ItineraryVersionListSchema.parse(data);
};

/** Field-level diff of this itinerary vs its parent version (Changes tab). */
export const getItineraryVersionDiff = async (id: string): Promise<ItineraryVersionDiff> => {
  const { data } = await apiClient.get<unknown>(`/itineraries/${id}/diff-from-parent`);
  return ItineraryVersionDiffSchema.parse(data);
};

/**
 * Reprice (Task 4): back-solve the discount/upsell that lands the itinerary's
 * grand total on `targetPaise` and apply it. Blocked (400) for a Fixed
 * Departure or a below-floor target - the caller surfaces that message
 * inline rather than treating it as a generic failure toast.
 */
export const repriceItinerary = async (
  id: string,
  payload: RepriceRequest,
): Promise<RepriceResponse> => {
  const { data } = await apiClient.post<unknown>(`/itineraries/${id}/reprice`, payload);
  return RepriceResponseSchema.parse(data);
};

/**
 * Fixed Departure removal-reprice: drop the visa and/or insurance supplement
 * and/or apply an other-discount. Re-applies from the clean quote each call.
 */
export const repriceFdItinerary = async (
  id: string,
  payload: FdRepriceRequest,
): Promise<RepriceResponse> => {
  const { data } = await apiClient.post<unknown>(`/itineraries/${id}/reprice/fd`, payload);
  return RepriceResponseSchema.parse(data);
};

/** Clear an active reprice: nulls the marker and zeroes the solved discount/upsell. */
export const clearItineraryReprice = async (id: string): Promise<void> => {
  await apiClient.delete(`/itineraries/${id}/reprice`);
};

// --- Reprice approvals queue (Task 7) -------------------------------------
// Dedicated queue for a >2%-of-margin discount reprice. Separate from the
// per-itinerary `/approvals` sub-resource above - server-side reviewer-scoped
// (ADMIN sees all, SALES_LEAD sees rows assigned to them, SALES_AGENT sees
// only their own submissions, read-only).

export const listRepriceApprovals = async (
  query: RepriceApprovalListQuery,
): Promise<RepriceApprovalListResponse> => {
  const { data } = await apiClient.get<unknown>('/itinerary-reprice-approvals', {
    params: query,
  });
  return RepriceApprovalListResponseSchema.parse(data);
};

/** Reviewer-scoped pending queue depth, same scoping as `listRepriceApprovals`. */
export const getRepriceApprovalsPendingCount = async (): Promise<RepriceApprovalPendingCount> => {
  const { data } = await apiClient.get<unknown>('/itinerary-reprice-approvals/pending-count');
  return RepriceApprovalPendingCountSchema.parse(data);
};

/** Admin or the assigned SALES_LEAD only; remark optional. Does not publish. */
export const approveRepriceApproval = async (
  id: string,
  payload: RepriceApprovalApproveRequest,
): Promise<RepriceApprovalListItem> => {
  const { data } = await apiClient.post<unknown>(
    `/itinerary-reprice-approvals/${id}/approve`,
    payload,
  );
  return RepriceApprovalListItemSchema.parse(data);
};

/** Admin or the assigned SALES_LEAD only; remark REQUIRED - clears the reprice. */
export const rejectRepriceApproval = async (
  id: string,
  payload: RepriceApprovalRejectRequest,
): Promise<RepriceApprovalListItem> => {
  const { data } = await apiClient.post<unknown>(
    `/itinerary-reprice-approvals/${id}/reject`,
    payload,
  );
  return RepriceApprovalListItemSchema.parse(data);
};

// ===== Void-a-won-deal approvals =====

/** Raise a request to void a won deal (for an itinerary amendment). */
export const requestVoidApproval = async (
  payload: VoidApprovalCreateRequest,
): Promise<VoidApprovalListItem> => {
  const { data } = await apiClient.post<unknown>('/itinerary-void-approvals', payload);
  return VoidApprovalListItemSchema.parse(data);
};

export const listVoidApprovals = async (
  query: VoidApprovalListQuery,
): Promise<VoidApprovalListResponse> => {
  const { data } = await apiClient.get<unknown>('/itinerary-void-approvals', { params: query });
  return VoidApprovalListResponseSchema.parse(data);
};

export const getVoidApprovalsPendingCount = async (): Promise<VoidApprovalPendingCount> => {
  const { data } = await apiClient.get<unknown>('/itinerary-void-approvals/pending-count');
  return VoidApprovalPendingCountSchema.parse(data);
};

/** Admin or the assigned SALES_LEAD only; runs the void executor. */
export const approveVoidApproval = async (
  id: string,
  payload: VoidApprovalApproveRequest,
): Promise<VoidApprovalListItem> => {
  const { data } = await apiClient.post<unknown>(
    `/itinerary-void-approvals/${id}/approve`,
    payload,
  );
  return VoidApprovalListItemSchema.parse(data);
};

/** Admin or the assigned SALES_LEAD only; remark REQUIRED. */
export const rejectVoidApproval = async (
  id: string,
  payload: VoidApprovalRejectRequest,
): Promise<VoidApprovalListItem> => {
  const { data } = await apiClient.post<unknown>(`/itinerary-void-approvals/${id}/reject`, payload);
  return VoidApprovalListItemSchema.parse(data);
};

/**
 * Pricing audit log rows for an itinerary (admin Logs tab). One row per
 * price-change event: customer reprice on the share page, agent republish,
 * admin money edit. Rows created at check() time sit in "pending
 * acceptance" (`confirmedAt = null`) until the customer clicks Confirm.
 */
export interface PricingLogComponentChange {
  id: string;
  componentId: string;
  componentKind: string;
  componentTitle: string;
  previousCostPaise: string;
  newCostPaise: string;
  deltaPaise: string;
  source: string;
}
export interface PricingLogRow {
  id: string;
  itineraryId: string;
  revisionId: string | null;
  changeSource:
    | 'SHARE_PAY_NOW_REPRICE'
    | 'SHARE_PAY_PLAN_I1_REPRICE'
    | 'PREVIEW_PUBLISH'
    | 'ADMIN_MANUAL_EDIT';
  actorKind: 'AGENT' | 'CUSTOMER' | 'SYSTEM';
  actorUserId: string | null;
  actorEmail: string | null;
  actorLabel: string | null;
  shareToken: string | null;
  confirmedAt: string | null;
  confirmedBy: string | null;
  previousNetCostPaise: string;
  newNetCostPaise: string;
  previousNetMarkupPaise: string;
  newNetMarkupPaise: string;
  previousGstPaise: string;
  newGstPaise: string;
  previousTcsPaise: string;
  newTcsPaise: string;
  previousGrandTotalPaise: string;
  newGrandTotalPaise: string;
  notes: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  componentChanges: PricingLogComponentChange[];
}
export const getItineraryPricingLogs = async (id: string): Promise<PricingLogRow[]> => {
  const { data } = await apiClient.get<PricingLogRow[]>(`/itineraries/${id}/pricing-logs`);
  return data;
};

/**
 * Read the persisted PaymentPlan for the Pricing tab (admin + sales
 * agent). Once a plan exists, the manual paymentSchedule editor is
 * hidden for everyone - the plan is the source of truth.
 */
export interface AdminPaymentPlanInstalment {
  id: string;
  seq: number;
  label: string;
  amountPaise: string;
  paidAmountPaise: string | null;
  currencyCode: string;
  dueDate: string;
  isNonRefundable: boolean;
  status: 'PENDING' | 'ACTIVE' | 'PAID' | 'LAPSED' | 'CANCELLED';
  paidAt: string | null;
  /** Gateway link for THIS instalment. Only the next-payable instalment is
   *  minted a link; non-null once it is ACTIVE. */
  paymentUrl: string | null;
  qrImageUrl: string | null;
  /** True when link generation has repeatedly failed ("Link failed"). */
  linkFailed: boolean;
}
export interface AdminPaymentPlanView {
  id: string;
  status: 'PREVIEW' | 'LOCKED' | 'LAPSED' | 'DEFAULTED' | 'COMPLETED' | 'CANCELLED';
  tier: number;
  currencyCode: string;
  totalSalePaise: string;
  bookingAmountPaise: string;
  tcsPaise: string;
  balancePaise: string;
  flightComponentPaise: string;
  publishedAt: string;
  travelStartDate: string;
  daysToDeparture: number;
  flightWindowHours: number;
  lockedAt: string | null;
  completedAt: string | null;
  cancelledAt: string | null;
  cancellationReason: string | null;
  instalments: AdminPaymentPlanInstalment[];
}
export const getItineraryPaymentPlan = async (
  itineraryId: string,
): Promise<AdminPaymentPlanView | null> => {
  const { data } = await apiClient.get<AdminPaymentPlanView | null>(
    `/payments/plan/by-itinerary/${itineraryId}`,
  );
  return data;
};

/**
 * Propose lowering the first instalment (routes through the reprice-approval
 * queue, kind INSTALMENT_SPLIT). The shortfall shifts to instalment 2 and its due
 * date moves one day earlier. The plan only changes once the approval is approved
 * (an ADMIN proposer auto-approves). Returns the created approval row.
 */
export const proposeInstalmentSplit = async (
  itineraryId: string,
  body: InstalmentSplitProposeRequest,
): Promise<RepriceApprovalListItem> => {
  const { data } = await apiClient.post<unknown>(
    `/payments/plan/${itineraryId}/instalment-split`,
    body,
  );
  return RepriceApprovalListItemSchema.parse(data);
};

/**
 * Propose changing the due date of one or more UNPAID instalments (reprice queue,
 * kind INSTALMENT_DATE_CHANGE). Works on a PREVIEW plan or a LOCKED (won) plan; an
 * ADMIN proposer auto-approves. Returns the created approval row.
 */
export const proposeInstalmentDateChange = async (
  itineraryId: string,
  body: InstalmentDateChangeProposeRequest,
): Promise<RepriceApprovalListItem> => {
  const { data } = await apiClient.post<unknown>(
    `/payments/plan/${itineraryId}/instalment-dates`,
    body,
  );
  return RepriceApprovalListItemSchema.parse(data);
};

/** These fan out to many live TBO searches (hotels per city + flights per leg),
 *  so they need a far longer deadline than the default 30s CRUD timeout; the
 *  backend caps each individual TBO call. */
const LIVE_SEARCH_TIMEOUT = 150_000;

/** Auto-search + pre-fill cheapest hotels/flights for the saved plan (Step 2). */
export const autoSearchItinerary = async (id: string): Promise<ItineraryDetail> => {
  const { data } = await apiClient.post<unknown>(
    `/itineraries/${id}/auto-search`,
    {},
    {
      timeout: LIVE_SEARCH_TIMEOUT,
    },
  );
  return ItineraryDetailSchema.parse(data);
};

/** Preview cheapest hotels/flights WITHOUT persisting (Resync / draft staging). */
export const previewAutoSearch = async (
  id: string,
  body: ItineraryAutoSearchPreviewRequest,
): Promise<ItineraryAutoSearchPreviewResponse> => {
  const { data } = await apiClient.post<unknown>(`/itineraries/${id}/auto-search/preview`, body, {
    timeout: LIVE_SEARCH_TIMEOUT,
  });
  return ItineraryAutoSearchPreviewResponseSchema.parse(data);
};

/** Reconcile the day-by-day after the trip shape changed (cities/dates). Returns
 *  the delta to add + the stale component ids to drop, WITHOUT persisting. */
export const reconcileAutoSearch = async (id: string): Promise<ItineraryReconcileResponse> => {
  const { data } = await apiClient.post<unknown>(
    `/itineraries/${id}/auto-search/reconcile`,
    {},
    {
      timeout: LIVE_SEARCH_TIMEOUT,
    },
  );
  return ItineraryReconcileResponseSchema.parse(data);
};

/** Flights-only reconcile (fixed departures in LIVE mode): fetches fares for the
 *  configured legs without touching the template hotels / land package. */
export const reconcileAutoSearchFlights = async (
  id: string,
): Promise<ItineraryReconcileResponse> => {
  const { data } = await apiClient.post<unknown>(
    `/itineraries/${id}/auto-search/reconcile-flights`,
    {},
    {
      timeout: LIVE_SEARCH_TIMEOUT,
    },
  );
  return ItineraryReconcileResponseSchema.parse(data);
};

// --- Days (B5.D) ---------------------------------------------------------

const DayListSchema = z.array(ItineraryDayOutSchema);

export const listItineraryDays = async (itineraryId: string): Promise<ItineraryDayOut[]> => {
  const { data } = await apiClient.get<unknown>(`/itineraries/${itineraryId}/days`);
  return DayListSchema.parse(data);
};

export const createItineraryDay = async (
  itineraryId: string,
  payload: CreateItineraryDayRequest,
): Promise<ItineraryDayOut> => {
  const { data } = await apiClient.post<unknown>(`/itineraries/${itineraryId}/days`, payload);
  return ItineraryDayOutSchema.parse(data);
};

export const updateItineraryDay = async (
  itineraryId: string,
  dayId: string,
  payload: UpdateItineraryDayRequest,
): Promise<ItineraryDayOut> => {
  const { data } = await apiClient.patch<unknown>(
    `/itineraries/${itineraryId}/days/${dayId}`,
    payload,
  );
  return ItineraryDayOutSchema.parse(data);
};

export const deleteItineraryDay = async (itineraryId: string, dayId: string): Promise<void> => {
  await apiClient.delete(`/itineraries/${itineraryId}/days/${dayId}`);
};

// --- Components (read for now; full CRUD in B5.E) -------------------------

const ComponentListSchema = z.array(ItineraryComponentOutSchema);

export const listItineraryComponents = async (
  itineraryId: string,
  dayId?: string,
): Promise<ItineraryComponentOut[]> => {
  const { data } = await apiClient.get<unknown>(`/itineraries/${itineraryId}/components`, {
    params: dayId ? { dayId } : undefined,
  });
  return ComponentListSchema.parse(data);
};

export const createItineraryComponent = async (
  itineraryId: string,
  dayId: string | null,
  payload: CreateItineraryComponentRequest,
): Promise<ItineraryComponentOut> => {
  const { data } = await apiClient.post<unknown>(
    `/itineraries/${itineraryId}/components`,
    payload,
    {
      params: dayId ? { dayId } : undefined,
    },
  );
  return ItineraryComponentOutSchema.parse(data);
};

export const updateItineraryComponent = async (
  itineraryId: string,
  componentId: string,
  payload: UpdateItineraryComponentRequest,
): Promise<ItineraryComponentOut> => {
  const { data } = await apiClient.patch<unknown>(
    `/itineraries/${itineraryId}/components/${componentId}`,
    payload,
  );
  return ItineraryComponentOutSchema.parse(data);
};

export const deleteItineraryComponent = async (
  itineraryId: string,
  componentId: string,
): Promise<void> => {
  await apiClient.delete(`/itineraries/${itineraryId}/components/${componentId}`);
};

// --- Approvals (B5.F) ----------------------------------------------------

export const listItineraryApprovals = async (
  itineraryId: string,
): Promise<ItineraryApprovalListResponse> => {
  const { data } = await apiClient.get<unknown>(`/itineraries/${itineraryId}/approvals`);
  return ItineraryApprovalListResponseSchema.parse(data);
};

export const actOnItineraryApproval = async (
  itineraryId: string,
  payload: ItineraryApprovalActionRequest,
): Promise<ItineraryApprovalListResponse> => {
  const { data } = await apiClient.post<unknown>(`/itineraries/${itineraryId}/approvals`, payload);
  return ItineraryApprovalListResponseSchema.parse(data);
};

// --- Revisions / publish (B5.H) ------------------------------------------

const RevisionListSchema = z.array(ItineraryRevisionOutSchema);

export const listItineraryRevisions = async (
  itineraryId: string,
): Promise<ItineraryRevisionOut[]> => {
  const { data } = await apiClient.get<unknown>(`/itineraries/${itineraryId}/revisions`);
  return RevisionListSchema.parse(data);
};

export const checkPublishPrices = async (
  itineraryId: string,
): Promise<PublishPriceCheckResponse> => {
  // Live TBO flight + hotel reprice can take well over the default 30s CRUD
  // timeout (each hotel Search may wait up to ~23s upstream).
  const { data } = await apiClient.post<unknown>(
    `/itineraries/${itineraryId}/publish/check`,
    {},
    { timeout: LIVE_SEARCH_TIMEOUT },
  );
  const parsed = PublishPriceCheckResponseSchema.safeParse(data);
  if (!parsed.success) {
    const issue = parsed.error.issues[0];
    throw {
      statusCode: 502,
      message: `Publish price check returned an unexpected response${
        issue ? `: ${issue.path.join('.') || 'body'} ${issue.message}` : ''
      }`,
      code: 'invalid_response',
    };
  }
  return parsed.data;
};

export const publishItinerary = async (
  itineraryId: string,
  payload: PublishItineraryRequest,
): Promise<PublishItineraryResponse> => {
  const { data } = await apiClient.post<unknown>(`/itineraries/${itineraryId}/publish`, payload);
  return PublishItineraryResponseSchema.parse(data);
};

/**
 * Mint a pre-publish preview link. Read-only on the server: it publishes
 * nothing, sends no customer email and mints no payment link - it just renders
 * the customer share page from the current draft.
 */
export const createItineraryPreviewLink = async (
  itineraryId: string,
): Promise<ItineraryPreviewLinkResponse> => {
  const { data } = await apiClient.post<unknown>(`/itineraries/${itineraryId}/preview-link`, {});
  return ItineraryPreviewLinkResponseSchema.parse(data);
};
